// @ts-nocheck
import design from "@store/Themes/common/styleguide/config/design";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue, Option } from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable from "@insite/mobius/Clickable/Clickable";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import getButtonSizeVariantField from "@store/Framework/Common/Fields/Toolkit/buttonSizeVariant";
import { getContextualCssUnitField } from "@store/Framework/Common/Utilities/FieldHelpers";
import Card from "@store/Library/Widgets/Card/Card";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import CardHeader from "@store/Library/Widgets/Card/CardHeader";
import Button from "@store/MobiusOverrides/Button/Button";
import colors from "@store/Themes/common/styleguide/config/colors";
import parse from "html-react-parser";
import * as cssLinter from "css";

import React from "react";
import { css, FlattenSimpleInterpolation, useTheme } from "styled-components";

const enum fields {
    title = "title",
    subtitle = "subtitle",
    backgroundImage = "backgroundImage",
    background = "background",
    backgroundColor = "backgroundColor",
    borderRadius = "borderRadius",
    contentAlignment = "contentAlignment",
    width = "width",
    height = "height",
    buttonText = "buttonText",
    buttonBackgroundColor = "buttonBackgroundColor",
    buttonTextColor = "buttonTextColor",
    buttonHoverBackGroundColor = "buttonHoverBackGroundColor",
    buttonHoverTextColor = "buttonHoverTextColor",
    buttonBorderRadius = "buttonBorderRadius",
    buttonLink = "buttonLink",
    isCardClickable = "isCardClickable",
    cardLink = "cardLink",
    buttonSize = "buttonSize",
    elevation = "elevation",
    buttonBorderColor = "buttonBorderColor",
    buttonFontWeight = "buttonFontWeight",
    buttonInset = "buttonInset",
    addOverlayImage = "addOverlayImage",
    overlayImage = "overlayImage",
    overlayImageHeight = "overlayImageHeight",
    overlayImageWidth = "overlayImageWidth",
    overlayImageLeft = "overlayImageLeft",
    overlayImageTop = "overlayImageTop",
    useZoneButton = "useZoneButton",
    cardPadding = "cardPadding",
    customCSS = "customCSS",
}

const defaultCustomCss = `& {
}

.link-card {
}

.card {
}

.card-header {
}

.card-body {
}

.button {
}

.overlay-image {
}`;

export type Alignments =
    | "topCenter"
    | "center"
    | "bottomCenter"
    | "centerLeft"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight"
    | "centerRight";

export type Elevations = "small" | "medium" | "large" | "2" | "none";

type FieldProps = {
    [fields.title]: string;
    [fields.subtitle]: string;
    [fields.backgroundImage]: string;
    [fields.background]: "color" | "image";
    [fields.backgroundColor]: string;
    [fields.borderRadius]: string;
    [fields.buttonText]: string;
    [fields.width]: string;
    [fields.height]: string;
    [fields.buttonBackgroundColor]: string;
    [fields.buttonTextColor]: string;
    [fields.buttonHoverBackGroundColor]: string;
    [fields.buttonHoverTextColor]: string;
    [fields.buttonBorderRadius]: string;
    [fields.buttonLink]: LinkFieldValue;
    [fields.buttonSize]: "small" | "medium" | "large";
    [fields.cardLink]: LinkFieldValue;
    [fields.contentAlignment]: Alignments;
    [fields.elevation]: "small" | "medium" | "large" | "none";
    [fields.buttonInset]: string;
    [fields.buttonBorderColor]: string;
    [fields.buttonFontWeight]: number;
    [fields.addOverlayImage]: boolean;
    [fields.overlayImage]: string;
    [fields.overlayImageHeight]: string;
    [fields.overlayImageWidth]: string;
    [fields.overlayImageTop]: string;
    [fields.overlayImageLeft]: string;
    [fields.useZoneButton]: boolean;
    [fields.cardPadding]: string;
    [fields.customCSS]: string;
};

interface LinkCardProps extends WidgetProps {
    fields: FieldProps;
}

export const alignmentStylesDictionary: Record<Alignments, { justifyContent: string; alignItems: string }> = {
    center: {
        justifyContent: "center",
        alignItems: "center",
    },
    topCenter: {
        justifyContent: "flex-start",
        alignItems: "center",
    },
    centerRight: {
        justifyContent: "center",
        alignItems: "flex-end",
    },
    bottomCenter: {
        justifyContent: "flex-end",
        alignItems: "center",
    },
    centerLeft: {
        justifyContent: "center",
        alignItems: "flex-start",
    },
    topLeft: {
        justifyContent: "flex-start",
        alignItems: "flex-start",
    },
    bottomLeft: {
        justifyContent: "flex-end",
        alignItems: "flex-start",
    },
    topRight: {
        justifyContent: "flex-start",
        alignItems: "flex-end",
    },
    bottomRight: {
        justifyContent: "flex-end",
        alignItems: "flex-end",
    },
};

export const getAlignments = (alignment: Alignments): FlattenSimpleInterpolation => {
    const { justifyContent, alignItems } = alignmentStylesDictionary[alignment];
    return css`
        justify-content: ${justifyContent};
        align-items: ${alignItems};
    `;
};

const getOverlayImageStyles = (
    overlayImage: string,
    overlayImageHeight: string,
    overlayImageWidth: string,
    overlayImageLeft: string,
    overlayImageTop: string,
) => {
    return {
        css: css`
            position: absolute;
            background-image: url(${overlayImage});
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            top: ${overlayImageTop};
            left: ${overlayImageLeft};
            height: ${overlayImageHeight};
            width: ${overlayImageWidth};
        `,
    };
};

const LinkCard: React.FC<LinkCardProps> = ({
    id,
    fields: {
        title,
        subtitle,
        backgroundImage,
        background,
        backgroundColor,
        borderRadius,
        width,
        height,
        buttonText,
        buttonBackgroundColor,
        buttonTextColor,
        buttonHoverBackGroundColor,
        buttonHoverTextColor,
        buttonBorderRadius,
        buttonLink,
        contentAlignment,
        cardLink,
        buttonSize,
        elevation,
        buttonBorderColor,
        buttonFontWeight,
        buttonInset,
        addOverlayImage,
        overlayImage,
        overlayImageHeight,
        overlayImageWidth,
        overlayImageTop,
        overlayImageLeft,
        useZoneButton,
        cardPadding,
        customCSS,
    },
}) => {
    const { url: buttonURL } = useGetLink(buttonLink);
    const { url: cardURL } = useGetLink(cardLink);

    const theme = useTheme();

    const shadowDictionary: Record<Elevations, string> = {
        small: theme.shadows[1],
        medium: theme.shadows[2],
        large: theme.shadows[3],
        "2": theme.shadows["2"],
        none: "none",
    };

    backgroundColor = background === "color" ? backgroundColor : "transparent";
    backgroundImage = background === "image" ? backgroundImage : "";
    const overlayImageStyles = getOverlayImageStyles(
        overlayImage,
        overlayImageHeight,
        overlayImageWidth,
        overlayImageLeft,
        overlayImageTop,
    );
    const cardStyles = {
        css: css`
            width: ${width};
            height: ${height};
            border-radius: ${borderRadius};
            background-color: ${backgroundColor};
            background-image: url(${backgroundImage});
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            position: relative;
            ${cardPadding ? `padding: ${cardPadding};` : ""}
            box-shadow: ${shadowDictionary[elevation]};
            ${getAlignments(contentAlignment)}
        `,
    };

    const headerStyles = {
        css: css`
            display: flex;
            ${alignmentStylesDictionary[contentAlignment]};
        `,
    };
    const bodyComponentStyles = {
        css: css`
            display: flex;

            ${alignmentStylesDictionary[contentAlignment]};
        `,
    };
    const buttonStyles = {
        css: css`
            background-color: ${buttonBackgroundColor || "primary"};
            position: relative;
            color: ${buttonTextColor || "white"};
            left: ${buttonInset};

            ${borderRadius ? `border-radius: ${buttonBorderRadius}` : ""};
            font-weight: ${buttonFontWeight || 400};
            ${buttonBorderColor ? `border-color: ${buttonBorderColor};` : ""};
            &:hover {
                border-color: ${buttonHoverBackGroundColor || buttonBorderColor || getColor("primary")};
                background-color: ${buttonHoverBackGroundColor || getColor("primary")};
                color: ${buttonHoverTextColor || "white"};
            }
        `,
    };

    const clickableStyles = {
        css: css`
            ${cardURL ? `` : "cursor: default;"}
            position: relative;
        `,
    };

    const customCssWrapper = {
        css: css`
            ${customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <Clickable href={cardURL || undefined} {...clickableStyles}>
                <Card size="xsmall" direction="column" extendedStyles={cardStyles} className="card">
                    {addOverlayImage && <StyledWrapper {...overlayImageStyles} className="overlay-image" />}
                    <CardHeader extendedStyles={headerStyles} className="card-header">
                        <Typography>{parse(title, parserOptions)}</Typography>
                    </CardHeader>
                    <CardBodyComponent extendedStyles={bodyComponentStyles} className="card-body">
                        <Typography>{parse(subtitle, parserOptions)}</Typography>
                    </CardBodyComponent>
                    <CardBodyComponent extendedStyles={bodyComponentStyles} className="card-body">
                        {buttonURL && !useZoneButton && (
                            <Clickable href={buttonURL}>
                                <Button sizeVariant={buttonSize} {...buttonStyles} className="button">
                                    {buttonText}
                                </Button>
                            </Clickable>
                        )}
                        {useZoneButton && <Zone contentId={id} zoneName="Button" />}
                    </CardBodyComponent>
                </Card>
            </Clickable>
        </StyledWrapper>
    );
};

export const widthField = getContextualCssUnitField(fields.width);
export const heightField = getContextualCssUnitField(fields.height);
export const borderRadiusField = getContextualCssUnitField(fields.borderRadius);
export const buttonBorderRadiusField = getContextualCssUnitField(fields.buttonBorderRadius);
export const buttonInsetField = getContextualCssUnitField(fields.buttonInset);

const overlayImagyHeightField = getContextualCssUnitField(fields.overlayImageHeight);
const overlayImageWidthField = getContextualCssUnitField(fields.overlayImageWidth);
const overlayImageTopField = getContextualCssUnitField(fields.overlayImageTop);
const overlayImageLeftField = getContextualCssUnitField(fields.overlayImageLeft);

export const elevationOptions: Option<Elevations>[] = [
    { displayName: "Low", value: "small" },
    { displayName: "Moderate", value: "medium" },
    { displayName: "High", value: "large" },
    { displayName: "None", value: "none" },
];
export const alignmentOptions: Option<Alignments>[] = [
    { displayName: "Center", value: "center" },
    { displayName: "Center Left", value: "centerLeft" as const },
    { displayName: "Center Right", value: "centerRight" },
    { displayName: "Top Center", value: "topCenter" },
    { displayName: "Bottom Center", value: "bottomCenter" },
    { displayName: "Top Left", value: "topLeft" },
    { displayName: "Top Right", value: "topRight" },
    { displayName: "Bottom Left", value: "bottomLeft" },
    { displayName: "Bottom Right", value: "bottomRight" },
];
const sizeVariantField = getButtonSizeVariantField(fields.buttonSize);

const buttonFontWeightOptions: Option<number>[] = Object.entries(design?.typography?.weight || {}).map(
    ([key, value]) => ({
        displayName: key,
        value,
    }),
);

const layoutTab: TabDefinition = {
    displayName: "Layout",
    sortOrder: 0,
};

const contentTab: TabDefinition = {
    displayName: "Content",
    sortOrder: 1,
};

const buttonTab: TabDefinition = {
    displayName: "Button",
    sortOrder: 2,
};

const imageTab: TabDefinition = {
    displayName: "Overlay Image",
    sortOrder: 3,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 4,
};
const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: LinkCard,
    definition: {
        group: "Common",
        icon: "LinkCard",
        displayName: "Link Card",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.contentAlignment,
                displayName: "Alignment",
                editorTemplate: "DropDownField",
                defaultValue: "topCenter",
                fieldType: "Contextual",
                options: alignmentOptions,
                tab: layoutTab,
            },

            {
                displayName: "Card Width",
                ...widthField.definition,
                defaultValue: "35rem",

                tab: layoutTab,
            },

            {
                displayName: "Card Height",
                ...heightField.definition,
                defaultValue: "22rem",
                tab: layoutTab,
            },
            {
                displayName: "Card Link",
                name: fields.cardLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Url",
                },
                tab: layoutTab,
            },
            {
                displayName: "Card Border Radius",
                ...borderRadiusField.definition,
                defaultValue: "0.75rem",
                tab: layoutTab,
            },

            {
                fieldType: "Translatable",
                displayName: "Title",
                editorTemplate: "RichTextField",
                defaultValue: "<h3>The Title</h3>",
                name: fields.title,
                tab: contentTab,
            },
            {
                displayName: "Subtitle",
                editorTemplate: "RichTextField",
                defaultValue: "</p>Subtitle</p>",
                fieldType: "Translatable",
                name: fields.subtitle,
                tab: contentTab,
            },
            {
                name: fields.elevation,
                displayName: "Elevation",
                editorTemplate: "DropDownField",
                options: elevationOptions,
                defaultValue: "large",
                fieldType: "Contextual",
                tab: layoutTab,
            },
            {
                name: fields.background,
                fieldType: "Contextual",
                displayName: "Background",
                editorTemplate: "RadioButtonsField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "color",
                tab: layoutTab,
            },
            {
                name: fields.backgroundColor,
                fieldType: "Contextual",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "#fafafa",
                isVisible: ({ fields: { background } }) => background === "color",
                tab: layoutTab,
            },
            {
                name: fields.backgroundImage,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: ({ fields: { background } }) => background === "image",
                tab: layoutTab,
            },
            {
                displayName: "Card Padding",
                name: fields.cardPadding,
                fieldType: "Contextual",
                editorTemplate: "TextField",
                defaultValue: "2rem",
                tab: layoutTab,
            },
            {
                name: fields.useZoneButton,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: buttonTab,
                sortOrder: 0,
            },
            {
                displayName: "Button Inset",
                ...buttonInsetField.definition,
                fieldType: "Contextual",
                defaultValue: "0",
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonBackgroundColor,
                fieldType: "Contextual",
                editorTemplate: "CustomColorPickerField",
                defaultValue: colors?.common?.background || "",
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonTextColor,
                fieldType: "Contextual",
                editorTemplate: "CustomColorPickerField",
                defaultValue: colors?.primary?.main || "",
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonHoverBackGroundColor,
                fieldType: "Contextual",
                editorTemplate: "CustomColorPickerField",
                defaultValue: colors?.secondary?.main || "",
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonHoverTextColor,
                fieldType: "Contextual",
                editorTemplate: "CustomColorPickerField",
                defaultValue: colors?.common?.background || "",
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                displayName: "Button Border Radius",
                ...buttonBorderRadiusField.definition,
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },

            {
                ...sizeVariantField.definition,
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },

            {
                name: fields.buttonLink,
                fieldType: "Contextual",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Url",
                },
                sortOrder: 1,
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonBorderColor,
                fieldType: "Contextual",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "",
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonFontWeight,
                fieldType: "Contextual",
                editorTemplate: "DropDownField",
                defaultValue: 400,
                options: buttonFontWeightOptions,
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.buttonText,
                fieldType: "Translatable",
                displayName: "Button Text",
                editorTemplate: "TextField",
                defaultValue: "Click me",
                sortOrder: 0,
                tab: buttonTab,
                isVisible: ({ fields: { useZoneButton } }) => !useZoneButton,
            },
            {
                name: fields.addOverlayImage,
                fieldType: "Contextual",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: imageTab,
            },
            {
                name: fields.overlayImage,
                fieldType: "Contextual",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: ({ fields: { addOverlayImage } }) => addOverlayImage,
                tab: imageTab,
            },
            {
                ...overlayImagyHeightField.definition,
                defaultValue: "100%",
                isVisible: ({ fields: { addOverlayImage } }) => addOverlayImage,
                tab: imageTab,
            },
            {
                ...overlayImageWidthField.definition,
                defaultValue: "100%",
                isVisible: ({ fields: { addOverlayImage } }) => addOverlayImage,
                tab: imageTab,
            },
            {
                ...overlayImageTopField.definition,
                defaultValue: "100%",
                isVisible: ({ fields: { addOverlayImage } }) => addOverlayImage,
                tab: imageTab,
            },
            {
                ...overlayImageLeftField.definition,
                defaultValue: "100%",
                isVisible: ({ fields: { addOverlayImage } }) => addOverlayImage,
                tab: imageTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (!value) return "";
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }
                    return "Unable to parse CSS";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
